import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Calculator, Droplets, Syringe, FlaskConical, AlertTriangle, ChevronDown, ChevronUp, FileText, Globe, Mail, Phone, Instagram } from 'lucide-react';
import SyringeVisualization from './SyringeVisualization';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bab87e2b-5ccc-45fa-8d79-b588017c2dea/artifacts/8jjjj21p_ThePeptideCoach%28qr%20and%20website%20address%29.jpg";

const PeptideCalculator = () => {
  // State for form inputs
  const [peptideAmount, setPeptideAmount] = useState('2');
  const [customPeptideAmount, setCustomPeptideAmount] = useState('');
  const [waterAmount, setWaterAmount] = useState('1');
  const [customWaterAmount, setCustomWaterAmount] = useState('');
  const [doseAmount, setDoseAmount] = useState('50');
  const [customDoseAmount, setCustomDoseAmount] = useState('');
  const [doseUnit, setDoseUnit] = useState('mcg');
  const [syringeSize, setSyringeSize] = useState('0.3');
  
  // State for results
  const [result, setResult] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  
  // State for disclaimer accordion
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);

  // Get actual values (handling "other" selections)
  const getActualPeptideAmount = () => {
    if (peptideAmount === 'other') {
      return parseFloat(customPeptideAmount) || 0;
    }
    return parseFloat(peptideAmount) || 0;
  };

  const getActualWaterAmount = () => {
    if (waterAmount === 'other') {
      return parseFloat(customWaterAmount) || 0;
    }
    return parseFloat(waterAmount) || 0;
  };

  const getActualDoseAmount = () => {
    if (doseAmount === 'other') {
      return parseFloat(customDoseAmount) || 0;
    }
    return parseFloat(doseAmount) || 0;
  };

  // Calculate the dose
  const calculateDose = useCallback(() => {
    const peptideMg = getActualPeptideAmount();
    const waterMl = getActualWaterAmount();
    let doseMcg = getActualDoseAmount();
    const syringeMl = parseFloat(syringeSize);

    // Validate inputs
    if (peptideMg <= 0 || waterMl <= 0 || doseMcg <= 0) {
      setResult(null);
      setHasCalculated(true);
      return;
    }

    // Convert dose to mcg if in mg
    if (doseUnit === 'mg') {
      doseMcg = doseMcg * 1000;
    }

    // Calculate concentration (mcg per mL)
    const peptideMcg = peptideMg * 1000; // Convert mg to mcg
    const concentrationMcgPerMl = peptideMcg / waterMl;

    // Calculate volume needed in mL
    const volumeNeededMl = doseMcg / concentrationMcgPerMl;

    // Calculate units based on syringe size
    // 0.3 mL syringe = 30 units, 0.5 mL = 50 units, 1 mL = 100 units
    const unitsPerMl = 100; // Standard insulin syringe
    const unitsNeeded = volumeNeededMl * unitsPerMl;

    // Calculate max units for selected syringe
    const maxUnits = syringeMl * 100;

    setResult({
      units: Math.round(unitsNeeded * 10) / 10,
      volumeMl: Math.round(volumeNeededMl * 1000) / 1000,
      maxUnits: maxUnits,
      syringeSize: syringeMl,
      concentration: Math.round(concentrationMcgPerMl * 100) / 100,
      exceedsSyringe: unitsNeeded > maxUnits
    });
    setHasCalculated(true);
  }, [peptideAmount, customPeptideAmount, waterAmount, customWaterAmount, doseAmount, customDoseAmount, doseUnit, syringeSize]);

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      {/* Background decoration */}
      <div className="fixed inset-0 bg-gradient-radial pointer-events-none" />
      
      <div className="relative max-w-4xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex justify-center mb-6">
            <img 
              src={LOGO_URL} 
              alt="The Peptide Coach" 
              className="h-32 sm:h-40 md:h-48 w-auto object-contain rounded-lg"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-2">
            <span className="text-gradient-primary">Peptide Reconstitution Calculator</span>
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto">
            Calculate your precise peptide dosage with our easy-to-use reconstitution calculator
          </p>
        </div>

        {/* Short Notice - RUO Disclaimer */}
        <div className="mb-6 animate-fade-in">
          <div className="bg-warning/10 border-2 border-warning/50 rounded-lg p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-warning flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="text-foreground font-bold text-sm sm:text-base">IMPORTANT — RESEARCH USE ONLY</h3>
                <p className="text-muted-foreground text-sm sm:text-[15px] leading-relaxed">
                  This Dose Calculator is provided for research and educational purposes only (RUO).
                  It is NOT intended for human or animal use, and is NOT medical, clinical, or dosing advice.
                </p>
                <p className="text-muted-foreground text-sm sm:text-[15px] leading-relaxed">
                  By using this calculator, you agree to the full disclaimer below.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Calculator Card */}
        <Card className="card-elevated border-gradient animate-slide-up">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl text-foreground">
              <Calculator className="w-5 h-5 text-primary" />
              Dosage Calculator
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Input Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Peptide Vial Amount */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-foreground font-medium">
                  <FlaskConical className="w-4 h-4 text-primary" />
                  Peptide Vial Amount (mg)
                </Label>
                <Select value={peptideAmount} onValueChange={setPeptideAmount}>
                  <SelectTrigger className="bg-muted border-border hover:border-primary/50 transition-colors">
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 mg</SelectItem>
                    <SelectItem value="3">3 mg</SelectItem>
                    <SelectItem value="5">5 mg</SelectItem>
                    <SelectItem value="10">10 mg</SelectItem>
                    <SelectItem value="15">15 mg</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {peptideAmount === 'other' && (
                  <Input
                    type="number"
                    placeholder="Enter amount in mg"
                    value={customPeptideAmount}
                    onChange={(e) => setCustomPeptideAmount(e.target.value)}
                    className="bg-muted border-border mt-2"
                    min="0"
                    step="0.1"
                  />
                )}
              </div>

              {/* Bacteriostatic Water */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-foreground font-medium">
                  <Droplets className="w-4 h-4 text-primary" />
                  Bacteriostatic Water (mL)
                </Label>
                <Select value={waterAmount} onValueChange={setWaterAmount}>
                  <SelectTrigger className="bg-muted border-border hover:border-primary/50 transition-colors">
                    <SelectValue placeholder="Select amount" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 mL</SelectItem>
                    <SelectItem value="2">2 mL</SelectItem>
                    <SelectItem value="3">3 mL</SelectItem>
                    <SelectItem value="5">5 mL</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {waterAmount === 'other' && (
                  <Input
                    type="number"
                    placeholder="Enter amount in mL"
                    value={customWaterAmount}
                    onChange={(e) => setCustomWaterAmount(e.target.value)}
                    className="bg-muted border-border mt-2"
                    min="0"
                    step="0.1"
                  />
                )}
              </div>

              {/* Dose Amount */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-foreground font-medium">
                  <Syringe className="w-4 h-4 text-primary" />
                  What is the dose? (mcg/mg)
                </Label>
                <div className="flex gap-2">
                  <Select value={doseAmount} onValueChange={setDoseAmount}>
                    <SelectTrigger className="bg-muted border-border hover:border-primary/50 transition-colors flex-1">
                      <SelectValue placeholder="Select dose" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50 mcg</SelectItem>
                      <SelectItem value="100">100 mcg</SelectItem>
                      <SelectItem value="250">250 mcg</SelectItem>
                      <SelectItem value="500">500 mcg</SelectItem>
                      <SelectItem value="1000">1 mg</SelectItem>
                      <SelectItem value="2500">2.5 mg</SelectItem>
                      <SelectItem value="5000">5 mg</SelectItem>
                      <SelectItem value="10000">10 mg</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {doseAmount === 'other' && (
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="number"
                      placeholder="Enter dose"
                      value={customDoseAmount}
                      onChange={(e) => setCustomDoseAmount(e.target.value)}
                      className="bg-muted border-border flex-1"
                      min="0"
                      step="0.1"
                    />
                    <Select value={doseUnit} onValueChange={setDoseUnit}>
                      <SelectTrigger className="bg-muted border-border w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcg">mcg</SelectItem>
                        <SelectItem value="mg">mg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Syringe Size */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-foreground font-medium">
                  <Syringe className="w-4 h-4 text-primary" />
                  Syringe Size (mL)
                </Label>
                <Select value={syringeSize} onValueChange={setSyringeSize}>
                  <SelectTrigger className="bg-muted border-border hover:border-primary/50 transition-colors">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.3">0.3 mL (30 units)</SelectItem>
                    <SelectItem value="0.5">0.5 mL (50 units)</SelectItem>
                    <SelectItem value="1">1 mL (100 units)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Calculate Button */}
            <Button 
              onClick={calculateDose}
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-6 text-lg transition-all hover:shadow-glow"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculate
            </Button>
            
            {/* Acceptance by Use Notice */}
            <p className="text-center text-muted-foreground text-xs sm:text-sm">
              Use of this tool constitutes acceptance of the disclaimer above.
            </p>

            {/* Results Section */}
            {hasCalculated && (
              <div className="mt-8 animate-scale-in">
                <h3 className="text-xl font-bold text-center text-foreground mb-6">Result:</h3>
                
                {result ? (
                  <div className="space-y-6">
                    {/* Syringe Visualization */}
                    <SyringeVisualization 
                      units={result.units} 
                      maxUnits={result.maxUnits}
                      syringeSize={result.syringeSize}
                    />
                    
                    {/* Result Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      <div className="bg-muted/50 rounded-lg p-4 text-center">
                        <p className="text-muted-foreground text-sm">Volume to Inject</p>
                        <p className="text-2xl font-bold text-primary">{result.volumeMl} mL</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 text-center">
                        <p className="text-muted-foreground text-sm">Concentration</p>
                        <p className="text-2xl font-bold text-accent">{result.concentration} mcg/mL</p>
                      </div>
                    </div>
                    
                    {/* Warning if exceeds syringe */}
                    {result.exceedsSyringe && (
                      <div className="bg-destructive/20 border border-destructive/50 rounded-lg p-4 text-center">
                        <p className="text-destructive font-medium">
                          ⚠️ Warning: The calculated dose ({result.units} units) exceeds your syringe capacity ({result.maxUnits} units).
                        </p>
                        <p className="text-muted-foreground text-sm mt-2">
                          Consider using a larger syringe or splitting into multiple injections.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground">
                    <p>Please enter valid values to calculate dosage.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Full Disclaimer Accordion */}
        <Card className="mt-6 card-elevated border-gradient animate-slide-up">
          <Collapsible open={isDisclaimerOpen} onOpenChange={setIsDisclaimerOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full p-4 sm:p-6 flex items-center justify-between text-left hover:bg-muted/30 transition-colors rounded-lg">
                <div className="flex items-center gap-2 sm:gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-semibold text-base sm:text-lg">View Full Disclaimer & Waiver</span>
                </div>
                {isDisclaimerOpen ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 sm:px-6 pb-6 space-y-6">
                <div className="border-t border-border pt-6">
                  <h3 className="text-foreground font-bold text-lg sm:text-xl mb-6">Dose Calculator Disclaimer & Waiver</h3>
                  
                  <div className="space-y-4 text-muted-foreground text-sm sm:text-[15px] leading-relaxed">
                    <p>
                      This Dose Calculator is provided by The Peptide Coach, Toby Schindelbeck, Schindelbeck, Inc., WellEvate HRT Advocates LLC, and their respective affiliates, officers, directors, employees, agents, contractors, successors, and assigns (collectively, the "Released Parties").
                    </p>
                    
                    <p className="text-foreground font-medium">
                      By accessing or using this Dose Calculator, you acknowledge, understand, and agree to the following:
                    </p>
                    
                    {/* Section 1 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">1. Research Use Only (RUO)</h4>
                      <p>
                        This calculator is provided strictly for informational, educational, and research demonstration purposes only.
                        It is NOT intended for human or animal use, and is not designed, validated, approved, or authorized for clinical, therapeutic, diagnostic, or medical applications of any kind.
                      </p>
                    </div>
                    
                    {/* Section 2 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">2. Not Medical or Clinical Advice</h4>
                      <p>
                        This calculator does NOT provide medical, clinical, pharmaceutical, or dosing advice.
                        Nothing generated by this tool should be interpreted as a recommendation, prescription, or guidance for diagnosis, treatment, or health decision-making.
                        All outputs are hypothetical examples only and must not be relied upon for real-world dosing decisions.
                      </p>
                    </div>
                    
                    {/* Section 3 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">3. No Warranties — "As Is"</h4>
                      <p>
                        This calculator and all outputs are provided "AS IS" and "AS AVAILABLE," without warranties of any kind, express or implied, including but not limited to accuracy, completeness, reliability, or fitness for a particular purpose.
                        The Released Parties make no representations or guarantees regarding the correctness of any calculations or results.
                      </p>
                    </div>
                    
                    {/* Section 4 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">4. Assumption of Risk & User Responsibility</h4>
                      <p>
                        You assume full responsibility and all risk for your use of this calculator and any actions taken based on its outputs.
                        You agree that you are solely responsible for consulting qualified professionals, including licensed healthcare providers or research professionals, before making any decisions based on information generated by this tool.
                      </p>
                    </div>
                    
                    {/* Section 5 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">5. Limitation of Liability</h4>
                      <p>
                        To the maximum extent permitted by law, the Released Parties shall not be liable for any direct, indirect, incidental, consequential, special, punitive, or exemplary damages — including personal injury, regulatory action, financial loss, or data misuse — arising from or related to your use of, or reliance on, this calculator.
                      </p>
                    </div>
                    
                    {/* Section 6 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">6. No Endorsement</h4>
                      <p>
                        This calculator does not endorse, recommend, promote, or sell any peptide, protocol, dosage, supplier, or product.
                        Nothing on this page constitutes medical, legal, or regulatory advice.
                      </p>
                    </div>
                    
                    {/* Section 7 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">7. Governing Law</h4>
                      <p>
                        This tool is operated under the laws of the United States and the State of Florida, without regard to conflict-of-law principles.
                      </p>
                    </div>
                    
                    {/* Section 8 */}
                    <div className="space-y-2">
                      <h4 className="text-foreground font-bold">8. Acceptance by Use</h4>
                      <p>
                        By continuing to use this Dose Calculator, you confirm that you have read, understood, and agreed to this disclaimer and waiver.
                        If you do not agree, you should discontinue use immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Instructions Section */}
        <Card className="mt-8 card-elevated border-gradient animate-slide-up">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">How to Use This Calculator</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
              <li className="leading-relaxed">
                <span className="text-foreground font-medium">Input the peptide mass (mg)</span> from the vial label.
              </li>
              <li className="leading-relaxed">
                <span className="text-foreground font-medium">Specify the bacteriostatic water volume (mL)</span> you'll add to reconstitute.
              </li>
              <li className="leading-relaxed">
                <span className="text-foreground font-medium">Input your desired dose</span> in micrograms (mcg) or milligrams (mg).
              </li>
              <li className="leading-relaxed">
                <span className="text-foreground font-medium">Choose your syringe size</span> (0.3 mL, 0.5 mL, or 1 mL).
              </li>
              <li className="leading-relaxed">
                <span className="text-foreground font-medium">Click Calculate</span> to see the exact units to draw.
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="mt-8 pb-8">
          <div className="border-t border-border pt-8">
            {/* Contact Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Website */}
              <a 
                href="https://www.ThePeptideCoach.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors group"
              >
                <Globe className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-foreground font-medium text-sm">Website</span>
                <span className="text-primary text-sm">www.ThePeptideCoach.com</span>
              </a>
              
              {/* Email */}
              <a 
                href="mailto:Toby@ThePeptideCoach.com" 
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors group"
              >
                <Mail className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-foreground font-medium text-sm">Email</span>
                <span className="text-muted-foreground text-sm">Toby@ThePeptideCoach.com</span>
              </a>
              
              {/* Phone */}
              <a 
                href="sms:+19413891937" 
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors group"
              >
                <Phone className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-foreground font-medium text-sm">Text Only</span>
                <span className="text-muted-foreground text-sm">(941) 389-1937</span>
              </a>
              
              {/* Instagram */}
              <a 
                href="https://www.instagram.com/toby_thepeptidecoach" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors group"
              >
                <Instagram className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-foreground font-medium text-sm">Instagram</span>
                <span className="text-muted-foreground text-sm">@toby_thepeptidecoach</span>
              </a>
            </div>
            
            {/* Copyright */}
            <div className="text-center text-muted-foreground text-sm">
              <p>© {new Date().getFullYear()} The Peptide Coach. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PeptideCalculator;
